import { Link } from 'react-router-dom';
import { ARCANA_LABELS, CARDS, SUIT_LABELS, cardSlug, type Card, type Suit } from '@tarot/shared';
import { TarotCard } from '../../components/TarotCard';
import { useTrack } from '../../lib/track';
import { useStaticPageMeta } from '../../lib/meta';

/**
 * 타로 카드 도감 (78장) — 검색 유입용 콘텐츠 + 서비스 내 참고 자료.
 * "타로 XX 카드 의미" 검색을 받아 서비스로 유입시키는 것이 목적이라
 * 각 카드마다 개별 URL(/cards/:slug)을 갖는다.
 */
export function CardsPage() {
  useTrack('view:cards');
  useStaticPageMeta('/cards');

  const majors = CARDS.filter((c) => c.arcana === 'major');
  const bySuit = (suit: Suit) => CARDS.filter((c) => c.suit === suit);

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">타로 카드 78장 의미</h1>
        <p className="text-sm leading-relaxed text-moon-dim">
          타로 덱은 인생의 큰 흐름을 다루는 <b className="text-moon">메이저 아르카나 22장</b>과
          일상의 장면을 다루는 <b className="text-moon">마이너 아르카나 56장</b>으로 이루어져
          있어요. 카드를 누르면 정방향·역방향 의미를 볼 수 있어요.
        </p>
      </header>

      <Section
        title={ARCANA_LABELS.major}
        desc="0번 바보부터 21번 세계까지, 한 사람이 겪는 성장의 여정을 담은 22장이에요."
      >
        <CardGrid cards={majors} />
      </Section>

      {(Object.keys(SUIT_LABELS) as Suit[]).map((suit) => (
        <Section
          key={suit}
          title={`${SUIT_LABELS[suit].ko} (14장)`}
          desc={`${SUIT_LABELS[suit].theme}를 다루는 슈트예요.`}
        >
          <CardGrid cards={bySuit(suit)} />
        </Section>
      ))}
    </div>
  );
}

function Section({
  title,
  desc,
  children,
}: {
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-bold text-gold">{title}</h2>
        <p className="text-xs text-moon-dim">{desc}</p>
      </div>
      {children}
    </section>
  );
}

function CardGrid({ cards }: { cards: readonly Card[] }) {
  return (
    <ul className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
      {cards.map((c) => (
        <li key={c.id}>
          <Link
            to={`/cards/${cardSlug(c)}`}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 p-2 transition hover:border-gold/50"
          >
            <TarotCard drawn={{ cardId: c.id, reversed: false, position: 0 }} zoomable={false} />
            <span className="text-center text-[11px] font-medium leading-tight">{c.nameKo}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
